/**
 * Brand Identity & Messaging Constants
 * Single Source of Truth (SSOT) for all brand-related strings and marketing copy
 * 
 * To rebrand "Brand Personality" to "Brand Voice":
 * Change BRAND_ENHANCEMENT: 'Brand Voice' (line 13)
 */

// ============= Design System Color Palette =============
export const COLOR_PALETTE = {
  // Primary Brand Colors (HSL values for reference)
  PRIMARY_EMERALD: '#063C34',      // hsl(171, 82%, 13%)
  CHARCOAL: '#0A0A0A',             // hsl(0, 0%, 4%)
  WARM_WHITE: '#FAFAF8',           // hsl(60, 20%, 97%)
  
  // Secondary Colors
  SLATE_GRAY: '#475467',           // hsl(218, 17%, 34%)
  MUTED_MINT: '#C9FDF0',           // hsl(166, 93%, 89%)
  
  // CTA & Accent
  ELECTRIC_BLUE: '#3E7BFA',        // hsl(220, 95%, 61%)
  ELECTRIC_BLUE_HOVER: '#2563EB',  // hsl(217, 91%, 53%)
  
  // Feedback Colors
  SUCCESS: '#12B76A',              // hsl(152, 82%, 39%)
  WARNING: '#FEC84B',              // hsl(42, 99%, 65%)
  ERROR: '#D92D20',                // hsl(4, 74%, 49%)
  INFO: '#0EA5E9',                 // hsl(199, 89%, 48%)
  
  // Borders & Shadows
  BORDER_LIGHT: '#E5E7EB',         // hsl(220, 13%, 91%)
  BORDER_DEFAULT: '#D1D5DB',       // hsl(220, 13%, 85%)
} as const;

// ============= Core Product Names =============
export const BRANDING = {
  // Main product names (change these to rebrand)
  PRODUCT_NAME: 'GrowthSocial',
  COMPANY_NAME: 'GrowthSocial LLC',
  
  // Technology/Feature Names
  BRAND_ENHANCEMENT: 'Brand Personality', // ← Change this one place to rebrand everywhere
  DNA_TECHNOLOGY: 'ContentDNA™',
  DNA_TECHNOLOGY_SHORT: 'ContentDNA',
  
  // Taglines
  PRIMARY_TAGLINE: 'The AI Thought Leadership + Pipeline Attribution Engine for Growth Teams',
  SECONDARY_TAGLINE: 'Turn your GTM team into revenue-generating thought leaders',
  CATEGORY_TAGLINE: 'Generate pipeline, not likes. Prove ROI, not impressions.',
  
  // Value Propositions
  TARGET_MARKET: 'growth-focused businesses',
  TARGET_MARKET_SHORT: 'business teams',
  
} as const;

// ============= Marketing Copy =============
export const MARKETING = {
  // Social Proof Numbers
  CAMPAIGNS_ANALYZED: '1,000+',
  COMPANIES_USING: '500+',
  TIME_SAVED: '10+ hours per week',
  POSTS_GENERATED: '50,000+',
  
  // Value Props
  MAIN_BENEFIT: 'Automated social strategist that proves ROI',
  DNA_BENEFIT: 'Every post sounds like your brand, not generic automation',
  
  // Feature Descriptions
  DNA_DESCRIPTION: 'Proprietary Brand Personality technology that analyzes your existing content to extract your unique voice, tone, and messaging patterns.',
  DNA_DETAIL: 'Unlike generic tools that sound the same for everyone, Brand Personality learns what makes your brand distinct.',
  
  // Hero Copy
  HERO_HEADLINE: 'Turn Your Entire Team Into Aligned, High-Authority Thought Leaders',
  HERO_SUBHEADLINE: 'Multi-user AI engine. Role-based content. Company messaging brain. 30-day calendars for every team member. Drive awareness, pipeline, and trust—automatically.',
  
} as const;

// ============= Contact & URLs =============
export const COMPANY_INFO = {
  // Domains
  MAIN_DOMAIN: 'growthsocial.ai',
  APP_DOMAIN: 'growthsocial.app',
  TRACKING_DOMAIN: 't.growthsocial.ai',
  
  // URLs
  WEBSITE_URL: 'https://growthsocial.ai',
  BLOG_URL: 'https://growthsocial.ai/resources/blog',
  HELP_CENTER_URL: 'https://growthsocial.app/help',
  
  // Social Media
  TWITTER_HANDLE: '@GrowthSocial',
  LINKEDIN_URL: 'https://www.linkedin.com/company/growthsocial',
  
  // Legal
  COPYRIGHT_YEAR: '2025',
  COPYRIGHT_TEXT: '© 2025 GrowthSocial LLC',
  
  // Support
  SUPPORT_EMAIL: 'support@growthsocial.ai',
  SALES_EMAIL: 'sales@growthsocial.ai',
  HELLO_EMAIL: 'hello@growthsocial.ai',
  LEGAL_EMAIL: 'legal@growthsocial.ai',
  
} as const;

// ============= UI Button Labels =============
export const UI_LABELS = {
  // Brand Enhancement Feature
  ENHANCE_BUTTON: `Apply ${BRANDING.BRAND_ENHANCEMENT}`,
  ENHANCING_BUTTON: `Applying ${BRANDING.BRAND_ENHANCEMENT}...`,
  ENHANCED_SUCCESS: `${BRANDING.BRAND_ENHANCEMENT} Applied`,
  ENHANCED_DESCRIPTION: `Your content has been enhanced with your brand's unique voice.`,
  ENHANCED_ERROR: `Failed to apply ${BRANDING.BRAND_ENHANCEMENT}`,
  
  // DNA Setup
  DNA_REQUIRED: `${BRANDING.DNA_TECHNOLOGY} Required`,
  DNA_COMPLETE: `${BRANDING.DNA_TECHNOLOGY} Configured`,
  DNA_SETUP_PROMPT: `Complete ${BRANDING.DNA_TECHNOLOGY} to unlock ${BRANDING.BRAND_ENHANCEMENT}`,
  DNA_MISSING: `Complete ${BRANDING.DNA_TECHNOLOGY_SHORT}`,
  
  // Call to Actions
  GET_STARTED: 'Get Started Free',
  START_TRIAL: 'Start Free',
  EXPLORE_PRODUCT: 'Explore the Product',
  LEARN_MORE: 'Learn More',
  
} as const;

// ============= Email Templates =============
export const EMAIL_COPY = {
  SENDER_NAME: 'Customer Success, GrowthSocial',
  UNSUBSCRIBE_TEXT: `You're receiving this email because you signed up for a ${BRANDING.PRODUCT_NAME} trial.`,
  
  // Email Subjects
  WELCOME_SUBJECT: `Welcome to ${BRANDING.PRODUCT_NAME}!`,
  ACTIVATION_SUBJECT: `Your ${BRANDING.PRODUCT_NAME} account is ready`,
  TRIAL_REMINDER: `Your ${BRANDING.PRODUCT_NAME} trial ends tomorrow`,
  
  // Email Headlines
  WELCOME_HEADLINE: `Welcome to ${BRANDING.PRODUCT_NAME}`,
  ACTIVATION_HEADLINE: `Let's Get You Set Up`,
  
  // Email Signatures
  SIGNATURE_NAME: 'The GrowthSocial Team',
  SIGNATURE_TITLE: 'Customer Success',
  
} as const;

// ============= Feature Names =============
export const FEATURES = {
  // Core Features
  CONTENT_AUTOMATION: 'Content Automation',
  ROI_MEASUREMENT: 'ROI Measurement',
  EXPERT_EXECUTION: 'Expert Execution',
  
  // DNA Features
  DNA_ENGINE: `${BRANDING.BRAND_ENHANCEMENT} Engine`,
  DNA_ANALYSIS: `${BRANDING.DNA_TECHNOLOGY} Analysis`,
  DNA_TRAINING: `${BRANDING.DNA_TECHNOLOGY} Training`,
  
  // Publishing Features
  MULTI_PLATFORM: 'Multi-Platform Publishing',
  SMART_SCHEDULING: 'Smart Scheduling',
  APPROVAL_WORKFLOW: 'Approval Workflow',
  
  // Analytics Features
  ATTRIBUTION: 'Multi-Touch Attribution',
  ANALYTICS: 'Advanced Analytics',
  REPORTING: 'Custom Reporting',
  
} as const;

// ============= Helper Functions =============

/**
 * Get formatted campaign message with optional DNA branding
 */
export function getCampaignSuccessMessage(brandDnaApplied: boolean): string {
  const baseMessage = 'Campaign created successfully';
  const enhancement = brandDnaApplied 
    ? ` powered by ${BRANDING.BRAND_ENHANCEMENT}` 
    : ` (tip: add ${BRANDING.DNA_TECHNOLOGY_SHORT} for personalized content)`;
  return baseMessage + enhancement;
}

/**
 * Get prerequisite error message
 */
export function getPrerequisiteMessage(missing: string[]): string {
  return `Please complete: ${missing.join(', ')}`;
}

/**
 * Get branded feature name (for dynamic contexts)
 */
export function getBrandedFeature(feature: 'enhancement' | 'dna' | 'engine'): string {
  const map = {
    'enhancement': BRANDING.BRAND_ENHANCEMENT,
    'dna': BRANDING.DNA_TECHNOLOGY,
    'engine': `${BRANDING.BRAND_ENHANCEMENT} Engine`,
  };
  return map[feature];
}

/**
 * Get email sender info
 */
export function getEmailSender() {
  return {
    name: EMAIL_COPY.SENDER_NAME,
    email: COMPANY_INFO.SUPPORT_EMAIL,
  };
}

/**
 * Get full website URL with path
 */
export function getWebsiteUrl(path: string = ''): string {
  return `${COMPANY_INFO.WEBSITE_URL}${path}`;
}

// ============= Backward Compatible Exports =============
// For edge functions and components that import directly
export const PRIMARY_BRAND_NAME = BRANDING.PRODUCT_NAME;
